import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    EnvironmentsService,
    FileTransfersService,
    IOcbApiDescriptionParameter,
    IOcbFileTransfer,
    IOcbFileTransferApiKey
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';
import { FileTransferPickerComponent } from '../../shared';

@Component({
    selector: 'app-file-transfers-api-keys',
    templateUrl: './file-transfers-api-keys.component.html',
    styleUrls: ['./file-transfers-api-keys.component.scss'],
})
export class FileTransfersApiKeysComponent implements AfterViewInit {

    @ViewChild('apiKeys')
    public apiKeysGrid: DxDataGridComponent;
    @ViewChild('apiKeyValidation')
    public apiKeyValidationGroup: DxValidationGroupComponent;
    @ViewChild('fileTransferPicker')
    public fileTransferPicker: FileTransferPickerComponent;

    // Main Data-Grid
    public dataSource: IOcbFileTransferApiKey[] = null;

    // List of file transfers
    public fileTransfers: IOcbFileTransfer[] = null;
    public selectedFileTransfer: IOcbFileTransfer = null;

    // Popup New & Edit
    public isPopupVisible: boolean = false;
    public selectedApiKey: IOcbFileTransferApiKey = null;
    public isNew: boolean = null;

    public isDropDownBoxOpened: boolean = false;
    public isLoadPanelVisible: boolean = true;
    public currentEnvironmentName: string = '';

    private currentEnvironmentId: string = '';
    private requestedFileTransferId?: string;

    constructor(
        private route: ActivatedRoute,
        private environmentsService: EnvironmentsService,
        private fileTransfersService: FileTransfersService,
        private translateService: TranslateService) {

    }

    public ngAfterViewInit() {
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['fileTransfer']) this.requestedFileTransferId = queryParams['fileTransfer'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => this.currentEnvironmentName = env.name);
            this.loadFileTransfers();
        });
    }

    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            forkJoin([
                this.translateService.get('_commons.rename'),
                this.translateService.get('_commons.reset'),
                this.translateService.get('_commons.delete')
            ]).subscribe({
                next: (messages) => {
                    e.items = [
                        { text: messages[0], icon: 'edit', onItemClick: (ev) => this.displayEditPopup(ev, e.row.data) },
                        { text: messages[1], icon: 'pulldown', onItemClick: (ev) => this.resetApiKey(ev, e.row.data.id) },
                        { text: messages[2], icon: 'trash', onItemClick: (ev) => this.deleteApiKey(ev, e.row.data.id) },
                    ];
                },
                error: (err) => this.handleError(err)
            });
        }
    }

    public onRowDblClick(e) {
        if (e?.data) this.displayEditPopup(e, e.data);
    }

    public displayNewPopup(): void {
        if (this.selectedFileTransfer) {
            this.selectedApiKey = {};
            this.isNew = true;
            this.isPopupVisible = true;
        }
        else 
            this.translateService.get('_pickers.msgFileTransferRequired').subscribe(message => notify(message, 'error', 3000));
    }

    public displayEditPopup(e, apiKey: IOcbFileTransferApiKey): void {
        if (e?.event) e.event.stopPropagation();
        this.selectedApiKey = {
            id: apiKey.id,
            environmentId: apiKey.environmentId,
            environmentName: apiKey.environmentName,
            fileTransferId: apiKey.fileTransferId,
            fileTransferName: apiKey.fileTransferName,
            apiKey: apiKey.apiKey,
            description: apiKey.description,
        };
        this.isNew = false;
        this.isPopupVisible = true;
    }

    public createApiKey(): void {
        if (this.apiKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newApiKey: IOcbApiDescriptionParameter = { description: this.selectedApiKey.description };
            this.fileTransfersService.addApiKey(this.selectedFileTransfer.id, newApiKey)
                .pipe(
                    mergeMap(_ => this.translateService.get('apiKeys.msgApiKeyCreationComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateApiKey(): void {
        if (this.apiKeyValidationGroup.instance.validate().isValid) {
            this.isLoadPanelVisible = true;
            const newDescription: IOcbApiDescriptionParameter = { description: this.selectedApiKey.description };
            this.fileTransfersService.updateApiKey(this.selectedFileTransfer.id, this.selectedApiKey.id, newDescription)
                .pipe(
                    mergeMap(_ => this.translateService.get('apiKeys.msgApiKeyUpdateComplete')),
                    finalize(() => this.isLoadPanelVisible = false))
                .subscribe({
                    next: (message) => {
                        this.isPopupVisible = false;
                        this.loadDataSource();
                        notify(message, 'success', 3000);
                    },
                    error: (err) => this.handleError(err)
                });
        }
    }

    public deleteApiKey(e, id): void {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('apiKeys.msgDeleteConfirm'),
            this.translateService.get('apiKeys.msgDeleteConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the RESET request
                mergeMap(_ => this.fileTransfersService.deleteApiKey(this.selectedFileTransfer.id, id)),
                // Reload the email template
                mergeMap(_ => this.translateService.get('apiKeys.msgDeleteComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public resetApiKey(e, id): void {
        e.event.stopPropagation();
        forkJoin([
            this.translateService.get('apiKeys.msgResetConfirm'),
            this.translateService.get('apiKeys.msgResetConfirmTitle')
        ])
            .pipe(
                // Display confirm popup
                mergeMap(messages => fromPromise(confirm(messages[0], messages[1]))),
                filter(accepted => accepted),
                // Display loader
                tap(_ => this.isLoadPanelVisible = true),
                // Send the DELETE request
                mergeMap(_ => this.fileTransfersService.resetApiKey(this.selectedFileTransfer.id, id)),
                // Get the confirmation message
                mergeMap(_ => this.translateService.get('apiKeys.msgResetComplete')),
                // Hide loader
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (message) => {
                    this.loadDataSource();
                    notify(message, 'success', 3000);
                },
                error: (err) => this.handleError(err)
            });
    }

    public notifyCopy(): void { this.translateService.get('_clipboard.copied').subscribe(message => notify(message, 'success', 3000)); }

    public filterChanged(e): void {
        if (e) {
            this.selectedFileTransfer = e;
            this.isDropDownBoxOpened = false;
            this.loadDataSource();
        } else {
            this.dataSource = [];
        }
    }

    private loadFileTransfers(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, true)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.fileTransfers = res;
                    if (this.requestedFileTransferId) this.fileTransferPicker.selectFileTransfer(this.requestedFileTransferId);
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadDataSource(): void {
        if (this.selectedFileTransfer) {
            this.showLoading$('_commons.loading')
            .pipe(
                mergeMap(_ => this.fileTransfersService.getApiKeys(this.selectedFileTransfer.id)),
                finalize(() => this.apiKeysGrid.instance.endCustomLoading()))
                .subscribe({
                    next: (res) => this.dataSource = res,
                    error: (err) => this.handleError(err)
                });
        }
        else
            this.translateService.get('_pickers.msgFileTransferRequired').subscribe(message => notify(message, 'error', 3000));
    }

    private showLoading$(messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => this.apiKeysGrid.instance.beginCustomLoading(`${message}...`)),
        );
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) message = error.error.message;
            else if (error.error.hasOwnProperty('name')) message = error.error.name;
            else message = error.error;
        }
        else message = error;
        notify(message, 'error', 3000);
    }
}
